import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Search, Sparkles, Globe, Users, Building2, Loader2, CheckCircle, TrendingUp } from 'lucide-react';
import { useMarketDiscovery } from '@/hooks/useMarketDiscovery';

export function AIMarketDiscovery() {
  const { isDiscovering, discoveredLeads, discoverLeads } = useMarketDiscovery();
  const [industry, setIndustry] = useState('');
  const [targetMarket, setTargetMarket] = useState('');
  const [keywords, setKeywords] = useState('');

  const handleDiscover = async () => {
    if (!industry.trim()) return;
    await discoverLeads({
      industry: industry.trim(),
      targetMarket: targetMarket.trim() || undefined,
      keywords: keywords.trim() || undefined,
    });
  };

  return (
    <div className="space-y-6">
      <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Globe className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle className="flex items-center gap-2">
                AI Market Discovery
                <Badge variant="secondary" className="text-xs">Beta</Badge>
              </CardTitle>
              <CardDescription>
                Automatically discover and harvest leads from online sources
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="industry">Target Industry *</Label>
              <Input
                id="industry"
                placeholder="e.g., SaaS, Healthcare, E-commerce"
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="targetMarket">Target Market</Label>
              <Input
                id="targetMarket"
                placeholder="e.g., Small businesses, Enterprise, Startups"
                value={targetMarket}
                onChange={(e) => setTargetMarket(e.target.value)}
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="keywords">Additional Keywords</Label>
            <Textarea
              id="keywords"
              placeholder="e.g., CRM software, sales automation, B2B"
              value={keywords}
              onChange={(e) => setKeywords(e.target.value)}
              rows={2}
            />
          </div>

          <Button 
            onClick={handleDiscover} 
            disabled={isDiscovering || !industry.trim()}
            className="w-full"
          >
            {isDiscovering ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Discovering Leads...
              </>
            ) : (
              <>
                <Search className="mr-2 h-4 w-4" />
                Discover Leads
              </>
            )}
          </Button>

          {isDiscovering && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Sparkles className="h-4 w-4 animate-pulse text-primary" />
                AI is searching and analyzing online sources...
              </div>
              <Progress value={undefined} className="h-1" />
            </div>
          )}
        </CardContent>
      </Card>

      {discoveredLeads.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-500" />
              Discovered Leads ({discoveredLeads.length})
            </CardTitle>
            <CardDescription>
              These leads have been automatically added to your lead pool
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {discoveredLeads.map((lead) => (
                <div
                  key={lead.id}
                  className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-full bg-primary/10">
                      <Building2 className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium">{lead.company_name}</p>
                      {(lead.first_name || lead.last_name) && (
                        <p className="text-sm text-muted-foreground flex items-center gap-1">
                          <Users className="h-3 w-3" />
                          {[lead.first_name, lead.last_name].filter(Boolean).join(' ')}
                        </p>
                      )}
                      {lead.email && (
                        <p className="text-sm text-muted-foreground">{lead.email}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="text-right">
                      <div className="flex items-center gap-1 text-sm">
                        <TrendingUp className="h-3 w-3 text-green-500" />
                        <span className="font-medium">{lead.lead_score}</span>
                        <span className="text-muted-foreground">IQ</span>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {lead.ai_conversion_score}% conversion
                      </div>
                    </div>
                    <Badge 
                      variant={lead.lead_score >= 70 ? 'default' : lead.lead_score >= 40 ? 'secondary' : 'outline'}
                    >
                      {lead.lead_score >= 70 ? 'Hot' : lead.lead_score >= 40 ? 'Warm' : 'Cold'}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
